import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography
} from "@mui/material";

import ReviewTableShell from "../review-table/ReviewTableShell";
import CsvPager from "./CsvPager";

import {
  CSV_PAGE_SIZE,
  csvDescriptionFor,
  csvLineKeyFor,
  csvRouteFor,
  csvUsageTypeFor,
  csvVendorNameFor,
  formatCsvDate,
  formatCsvQuantity
} from "./csvReviewFormat";
import { reasonCopy } from "./reviewLineGates";
import { useCsvLinePages } from "./useCsvReview";

const failureMessage = (line, commitResult) => {
  const isStillFailing =
    !commitResult ||
    !["committed", "already_committed"].includes(commitResult.outcome);
  if (!isStillFailing) {
    return "Recorded.";
  }
  return (
    commitResult?.message ||
    line.commit_error?.message ||
    reasonCopy(commitResult?.reason || line.commit_error?.reason)
  );
};

const whyColumn = (commitResults) => ({
  id: "why",
  label: "Why",
  render: (line) => (
    <Typography variant="caption" color="error">
      {failureMessage(line, commitResults[csvLineKeyFor(line)])}
    </Typography>
  )
});

const dateColumn = (label = "Date") => ({
  id: "date",
  label,
  render: (line) => formatCsvDate(line.period_end_final)
});

// utilities_csv/office_csv (ticket #412): a row can draft several lines (one
// per usage reading), so a failed line also names the CSV row it came from --
// no other family's commit-failure line needs that, since every other family
// is one row per line.
const sourceRowColumn = {
  id: "sourceRow",
  label: "Row #",
  render: (line) => line.source_row_number ?? "—"
};

// One config per family instead of a growing isShipping-style boolean
// (ticket DlAT2n1ITtjB6BA9ntuJ) — a fourth family adds a fourth entry here.
const FAILED_COLUMNS_BY_FAMILY = {
  shipping_transactions: ({ commitResults }) => [
    dateColumn("Ship date"),
    { id: "route", label: "From → To", render: (line) => csvRouteFor(line) },
    {
      id: "quantity",
      label: "Weight",
      render: (line) => formatCsvQuantity(line)
    },
    whyColumn(commitResults)
  ],
  utilities_csv: ({ commitResults }) => [
    dateColumn(),
    sourceRowColumn,
    { id: "usageType", label: "Usage type", render: (line) => csvUsageTypeFor(line) },
    {
      id: "quantity",
      label: "Amount",
      render: (line) => formatCsvQuantity(line)
    },
    whyColumn(commitResults)
  ],
  office_csv: ({ commitResults }) => [
    dateColumn(),
    sourceRowColumn,
    {
      id: "subcategory",
      label: "Subcategory",
      render: (line) => line.subcategory_final || "—"
    },
    {
      id: "quantity",
      label: "Amount",
      render: (line) => formatCsvQuantity(line)
    },
    whyColumn(commitResults)
  ]
};

const DEFAULT_FAILED_COLUMNS = ({ commitResults }) => [
  dateColumn(),
  {
    id: "description",
    label: "Description",
    render: (line) => csvDescriptionFor(line)
  },
  {
    id: "quantity",
    label: "Amount",
    render: (line) => formatCsvQuantity(line)
  },
  {
    id: "vendor",
    label: "Vendor",
    render: (line) => csvVendorNameFor(line)
  },
  whyColumn(commitResults)
];

const failedColumns = ({ documentFamily, commitResults }) =>
  (FAILED_COLUMNS_BY_FAMILY[documentFamily] || DEFAULT_FAILED_COLUMNS)({
    commitResults
  });

// Lines a confirm tried and couldn't record (and that don't need a decision
// from anyone): each keeps its reason and its own Retry, so a failed line is
// never left invisible between the ready and recorded lists.
const CsvFailedLines = ({
  companyId,
  taskDocId,
  documentFamily,
  failedCount,
  refreshKey,
  commitResults,
  canRetry,
  isCommitting,
  onRetryLine
}) => {
  const pages = useCsvLinePages({
    companyId,
    taskDocId,
    filter: "failed",
    refreshKey
  });

  return (
    <Box sx={{ mb: 2 }}>
      <ReviewTableShell
        title="Couldn't be recorded"
        subtitle={`${failedCount} ${failedCount === 1 ? "line" : "lines"}`}
        needsAttention
        defaultExpanded
        banners={
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 1 }}>
            <CsvPager pages={pages} pageSize={CSV_PAGE_SIZE} />
            {pages.error && <Alert severity="error">{pages.error}</Alert>}
          </Box>
        }
        columns={failedColumns({ documentFamily, commitResults })}
        lines={pages.lines}
        getLineKey={csvLineKeyFor}
        actionsLabel="Actions"
        renderLineActions={(line) => (
          <Button
            size="small"
            disabled={!canRetry || isCommitting}
            onClick={() => onRetryLine({ rowId: line.rowId, lineId: line.id })}
          >
            Retry
          </Button>
        )}
        body={
          (!pages.lines.length &&
            (pages.isLoading ? (
              <CircularProgress size={20} />
            ) : (
              <Typography variant="body2" color="textSecondary">
                Nothing on this page.
              </Typography>
            ))) ||
          undefined
        }
      />
    </Box>
  );
};

export default CsvFailedLines;
